import { SERVER, SUBDOMAIN, isCarHost } from "../components/helpers/constants";
import {
  applyCmsSeoMeta,
  getCmsSeoForPath,
} from "../components/helpers/cmsSeoService";

const BROKER_URL_PATTERN =
  /^\/(broker|title|insurance|attorney)\/[^/]+(?:\/|$)/;

const setRobotsMetaTag = (content) => {
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = "robots";
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setMeta = (title, description, keywords = null) => {
  // Helper function to set or create meta tags
  const updateMetaTag = (name, content) => {
    let metaTag = document.querySelector(`meta[name="${name}"]`);
    if (metaTag) {
      metaTag.setAttribute("content", content);
    } else {
      metaTag = document.createElement("meta");
      metaTag.name = name;
      metaTag.content = content;
      document.head.appendChild(metaTag);
    }
  };

  // Update title and description for all specified meta tags
  updateMetaTag("title", title);
  document.title = title;
  updateMetaTag("description", description);
  updateMetaTag("og:title", title);
  updateMetaTag("og:description", description);
  updateMetaTag("twitter:title", title);
  updateMetaTag("twitter:description", description);

  const keywordsTag = document.querySelector('meta[name="keywords"]');
  if (keywords) {
    updateMetaTag("keywords", keywords);
  } else if (keywordsTag) {
    keywordsTag.remove();
  }
};

export const setMetaDataForSEO = (pathname, domainName) => {
  let schemaData = null;

  const hostname = (domainName || "").toLowerCase();
  let robotsContent;
  if (BROKER_URL_PATTERN.test(pathname)) {
    robotsContent = "noindex, nofollow";
  } else if (hostname.includes("staging") || SERVER !== "production") {
    robotsContent = "noindex";
  } else if (hostname.includes("lennar")) {
    robotsContent = "noindex, follow";
  } else {
    robotsContent = "index, follow";
  }
  setRobotsMetaTag(robotsContent);

  const cmsSeoRow = getCmsSeoForPath(pathname);
  if (cmsSeoRow && applyCmsSeoMeta(pathname, setMeta)) {
    return cmsSeoRow.schema_json || null;
  }

  if (isCarHost(hostname) && (pathname === "/" || pathname === "")) {
    setMeta(
      "C.A.R. Member Property Protection | EquityProtect",
      "Discover the EquityProtect property protection program for California REALTORS®, including member pricing, client benefits, and agent tools.",
    );
    return null;
  }

  switch (pathname) {
    case "/":
      setMeta(
        domainName?.includes(SUBDOMAIN?.provider)
          ? "Home Equity Protection by EquityProtect"
          : "EquityProtect | Home Equity Protection from Title Fraud",
        "Protect your home equity with EquityProtect's title protection solutions designed to secure ownership and reduce fraud risk.",
      );
      break;
    case "/how-it-works":
      setMeta(
        "How Home Equity Protection Works | EquityProtect",
        "Learn how EquityProtect helps secure home equity, protect ownership, and give homeowners control against title fraud.",
      );
      break;
    case "/pricing":
      setMeta(
        "Pricing - Home Equity Protection Pricing | EquityProtect",
        "Compare EquityProtect pricing and plans designed to help homeowners protect title, equity, and ownership from fraud.",
      );
      break;
    case "/create-account":
      setMeta(
        "Create Account | EquityProtect",
        "Create your account with EquityProtect to securely protect your home equity and gain access to advanced tools and services. Get started today!",
      );
      break;
    case "/convert":
      setMeta(
        "Activate Your Secure EquityProtect Dashboard",
        "Create your secure EquityProtect account to access your dashboard, monitor your property's protection, view risk scans, manage alerts, and more.",
      );
      break;
    case "/partner-activate":
      setMeta(
        "Activate Complete Protection | EquityProtect",
        "Set your password and upgrade partner monitoring to Complete Protection.",
      );
      break;
    case "/property-risk-management":
      setMeta(
        "Property Risk Management Services | EquityProtect",
        "EquityProtect offers expert property risk management services to help you protect your home equity. Take control of your investments with our reliable risk management solutions.",
      );
      break;
    case "/attorney-title-program/title-protection-real-estate-investors":
      setMeta(
        "Title Protection for Real Estate Investors | EquityProtect",
        "Real estate investors face growing title fraud risks. Learn how EquityProtect helps protect portfolios from unauthorized title changes.",
      );
      break;
    case "/attorney-title-program/title-protection-long-time-homeowners":
      setMeta(
        "Home Title Protection for Long-Time Homeowners | EquityProtect",
        "Long-time homeowners face rising title theft risks. Learn how EquityProtect helps safeguard your title, equity, and ownership control.",
      );
      break;
    case "/attorney-title-program/what-is-home-title-theft":
      setMeta(
        "Signs of Home Title Theft & How to Protect Yourself | EquityProtect",
        "Is your home at risk of fraud? Discover the growing threat of home title theft and how to safeguard your biggest investment with EquityProtect. Learn more.",
      );
      break;
    case "/industrypartner/title-protection-real-estate-investors":
      setMeta(
        "Title Protection for Real Estate Investors | EquityProtect",
        "Real estate investors face growing title fraud risks. Learn how EquityProtect helps protect portfolios from unauthorized title changes.",
      );
      break;
    case "/industrypartner/title-protection-long-time-homeowners":
      setMeta(
        "Home Title Protection for Long-Time Homeowners | EquityProtect",
        "Long-time homeowners face rising title theft risks. Learn how EquityProtect helps safeguard your title, equity, and ownership control.",
      );
      break;
    case "/title-protection-real-estate-investors":
      setMeta(
        "Title Protection for Real Estate Investors | EquityProtect",
        "Real estate investors face growing title fraud risks. Learn how EquityProtect helps protect portfolios from unauthorized title changes.",
      );
      break;
    case "/title-protection-long-time-homeowners":
      setMeta(
        "Home Title Protection for Long-Time Homeowners | EquityProtect",
        "Long-time homeowners face rising title theft risks. Learn how EquityProtect helps safeguard your title, equity, and ownership control.",
      );
      break;
    case "/what-is-home-title-theft":
      setMeta(
        "What Is Home Title Theft? Signs, Risks & Protection",
        "Learn what home title theft is, how deed fraud works, warning signs to watch for, and how proactive title protection keeps homeowners in control.",
      );
      break;
    case "/home-title-theft-risk-new-homeowners":
      setMeta(
        "Home Title Theft Risk for New Homeowners",
        "New homeowners can still face title fraud risk after closing. Learn why a mortgage does not fully protect your title and how proactive title protection keeps you in control.",
      );
      break;
    case "/title-theft-protection-real-estate-investors":
      setMeta(
        "Title Theft Protection for Real Estate Investors",
        "Protect rental properties, vacant homes, LLC-owned properties, and out-of-state investments from title theft risk with EquityProtect’s Proactive Title Lock protection.",
      );
      break;
    case "/why-monitoring-isnt-enough":
      setMeta(
        "Why Monitoring Isn't Enough | EquityProtect",
        "Monitoring only alerts you after fraud happens. Learn how EquityProtect Preventative Protection helps stop title fraud before it occurs.",
      );
      break;

    case "/for-service-provider":
      setMeta(
        "Home Equity Protection for Service Providers | EquityProtect",
        "Help clients protect home equity with EquityProtect's title protection solutions for ownership security and fraud prevention.",
      );
      break;

    case "/gallagher":
      setMeta(
        "Mike Gallagher Recommends EquityProtect | EquityProtect",
        "Discover if your property is at risk of title theft with the home equity protection solution recommended by Mike Gallagher. Get a free risk assessment now.",
      );
      break;

    case "/gorka":
      setMeta(
        "Dr. Sebastian Gorka Recommends EquityProtect | EquityProtect",
        "Find out if your property is vulnerable to title theft with the home equity protection trusted by Dr. Sebastian Gorka. Get your free risk assessment today.",
      );
      break;

    case "/blog":
      setMeta(
        "EquityProtect Blog | Home Equity Protection & Title Fraud",
        "Stay informed about title fraud, home equity protection, and real estate security.",
      );
      break;
    case "/blog/house-title-check-vs-title-protection":
      setMeta(
        "House Title Check vs. Title Protection: What's the Difference?",
        "Learn the difference between a one-time house title check and ongoing title protection. Discover which option offers greater long-term visibility into your property's ownership records and how EquityProtect helps homeowners stay informed.",
      );
      break;

    default:
      if (/^\/insurance\/[^/]+\/property-risk-report$/.test(pathname)) {
        setMeta(
          "Property Risk Management Services | EquityProtect",
          "Get a property risk report from EquityProtect to better understand title fraud exposure and protect your home equity.",
        );
      } else if (
        /^\/blog\/[^/]+$/.test(pathname) ||
        /^\/pages\/[^/]+$/.test(pathname)
      ) {
        // CMS detail pages set meta after content loads.
      } else {
        setMeta(
          "Home Lock - EquityProtect",
          "The First and Only True Property Title Lock - Prevents Home Stealing, Title Theft and Equity Protection",
        );
      }
      break;
  }
  return schemaData;
};
